import { FormControlLabel } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { Spacing } from "Infrastructure/Themes/Themes";
import type { ReactElement, ReactNode } from "react";
import { useIsPrintMode } from "./QuestionnaireContext";

export const printAnswerPaddingSx = { px: Spacing.M, py: Spacing.S };

type OptionStateParams = {
  isSelected: boolean;
  isDisabled?: boolean;
  idleBorderColor?: string;
};

/** Shared by the answer rows and the image cards, so every selectable answer marks its state the same way. */
export const getOptionStateSx = (theme: Theme, { isSelected, isDisabled = false, idleBorderColor = "transparent" }: OptionStateParams) => ({
  // The border is there even when idle, so selecting an answer does not shift its content.
  border: "1px solid",
  borderColor: isSelected ? theme.colors.brandLightBorder : idleBorderColor,
  bgcolor: isSelected ? "primary.light" : "common.white",
  cursor: isDisabled ? "default" : "pointer",
  transition: "background-color 0.15s",
  ...(!isSelected && !isDisabled && { "&:hover": { bgcolor: "grey.50" } }),
  "&:has(.Mui-focusVisible)": { outline: "2px solid", outlineColor: "primary.dark", outlineOffset: "2px" },
});

// The MUI default for an unchecked control is text.secondary, which is too light against white. The checked
// and disabled colours stay MUI's, their selectors outrank this one.
export const optionControlSx = {
  p: 0,
  color: "grey.600",
  "&:hover": { bgcolor: "transparent" },
  "& .MuiSvgIcon-root": { fontSize: 18 },
};

type Props = {
  control: ReactElement;
  label: ReactNode;
  value?: string;
  isSelected: boolean;
  disabled?: boolean;
};

export const QuestionOption = ({ control, label, value, isSelected, disabled }: Props) => {
  const isPrint = useIsPrintMode();

  return (
    <FormControlLabel
      value={value}
      control={control}
      label={label}
      disabled={disabled}
      sx={(theme) => ({
        ...getOptionStateSx(theme, { isSelected, isDisabled: disabled }),
        display: "flex",
        alignItems: "center",
        gap: "10px",
        m: 0,
        // The touch target means nothing on paper and would only stretch the PDF.
        minHeight: isPrint ? 0 : 38,
        ...{ px: Spacing.M, py: Spacing.S },
        borderRadius: "10px",
        breakInside: "avoid",
        "& .MuiFormControlLabel-label": {
          fontSize: 14,
          lineHeight: 1.4,
          color: "text.primary",
          // Medium rather than bold - bold widens the text and would re-wrap the selected answer.
          fontWeight: isSelected ? 500 : 400,
        },
      })}
    />
  );
};
